import React, { useState, useEffect, useRef } from 'react';

interface FAQItem {
  category: string;
  question: string;
  answer: string;
}

interface SearchSuggestion extends FAQItem {
  index: number;
}

const faqData: FAQItem[] = [
  // General - System
  {
    category: "General - System",
    question: "Where can I access the Employee Performance Management System?",
    answer: "You can access it via the login page using your Gentari credentials."
  },
  {
    category: "General - System",
    question: "How do I log in to the Employee Performance Management System?",
    answer: "Enter your Gentari email and an OTP number will be sent to your email."
  },
  {
    category: "General - System",
    question: "What if I face technical issues with the system?",
    answer: "Please contact the IT Helpdesk or HR support team for assistance."
  },
  // Target Setting
  {
    category: "Target Setting",
    question: "Who is eligible to participate in the target setting process?",
    answer: "All employees who meet the performance review criteria as communicated by HR."
  },
  {
    category: "Target Setting",
    question: "What is the timeline for completing target setting?",
    answer: "HR will send reminders with specific deadlines. Please refer to internal communications or system notifications. You can also view the timelines in the side navigation bar in the system."
  },
  {
    category: "Target Setting",
    question: "How do I draft and submit my OKRs?",
    answer: "You can draft your OKRs in the system. Submit them for approval by your supervisor."
  },
  {
    category: "Target Setting",
    question: "How do I draft if my OKRs has more than xx number of lines?",
    answer: "You are encouraged to simplify the OKR to ensure greater focus on the performance. However, should the situation be unavoidable, you can add as many as 10,000 words."
  },
  {
    category: "Target Setting",
    question: "What is the guideline for setting OKRs?",
    answer: "Set clear objectives aligned with enterprise goals, each supported by 2–5 measurable key results using SMART criteria (Specific, Measurable, Achievable, Relevant, Time-bound)."
  },
  {
    category: "Target Setting",
    question: "Can I edit my OKRs after submission?",
    answer: "You can edit them before submission for approval. After submission for approval, changes require supervisor consent and may need to be re-submitted."
  },
  {
    category: "Target Setting",
    question: "As superior, what if I have accidentally approved the wrong OKRs and require my subordinate to amend?",
    answer: "You are required to reach out to the HR support team to enable the form for the employee to amend and send for approval."
  },
  // Mid Year Review
  {
    category: "Mid Year Review",
    question: "What is required from me during the Mid-Year Review?",
    answer: "You need to complete the \"Result\" column and provide a self-assessment rating. Then, book a review session with your supervisor."
  },
  {
    category: "Mid Year Review",
    question: "What happens after I submit my review?",
    answer: "Your supervisor will provide an indicative rating and approve it in the system."
  },
  {
    category: "Mid Year Review",
    question: "What if I miss the deadline?",
    answer: "HR will send reminders, but late submissions may impact your performance tracking. Please complete it on time."
  },
  {
    category: "Mid Year Review",
    question: "As superior, will my subordinate receive notification on indicative overall rating that I have inserted?",
    answer: "No, the subordinates will not receive any intimation about the rating submitted, but will be notified once the ratings have been submitted."
  },
  // 360 Review
  {
    category: "360 Review",
    question: "What is the purpose of the 360 Review?",
    answer: "It provides feedback from peers, subordinates, and supervisors to support development and upskilling."
  },
  {
    category: "360 Review",
    question: "How do I nominate assessors for my 360 Review?",
    answer: "Use the nomination form in the system. Submit your proposed assessors for supervisor approval."
  },
  {
    category: "360 Review",
    question: "How does superior approve/reject the request?",
    answer: "Superior shall receive a notification via email. Superior can select the \"Approve/Reject\" option on the notification received."
  },
  {
    category: "360 Review",
    question: "How does employee change the nomination post approval by superior?",
    answer: "Employees are NOT ALLOWED to change nomination post approval."
  },
  {
    category: "360 Review",
    question: "What should an employee do if they send the request to the wrong superior for nomination approval?",
    answer: "Notify the focal person from Talent team to update the superior name."
  },
  {
    category: "360 Review",
    question: "What should an employee do if they nominate the wrong peer?",
    answer: "Notify the focal person from Talent team to validate the nomination list."
  },
  {
    category: "360 Review",
    question: "How can an employee check the status of their request?",
    answer: "Employee can check in the system."
  },
  {
    category: "360 Review",
    question: "How can a superior check the status of nominations from all their subordinates?",
    answer: "Employee can check in the system."
  },
  {
    category: "360 Review",
    question: "How many forms do employees need to fill out?",
    answer: "Employees need to fill up 2 forms:\ni. 360 Nomination Approval\nii. 360 Feedback form"
  },
  {
    category: "360 Review",
    question: "What happens if the superior does not approve it on time?",
    answer: "You will not receive your individual rating or bonus payout. We encourage you to complete the approval within the timeline set by the Talent Department."
  },
  {
    category: "360 Review",
    question: "Who will be able to see my feedback?",
    answer: "Your feedback will remain anonymous and will be visible to your superior(s) and HR Admin."
  },
  {
    category: "360 Review",
    question: "Will I be notified when feedback forms are sent to assessors?",
    answer: "Yes, the system will auto-launch the forms. You are responsible for following up with assessors to ensure completion."
  },
  {
    category: "360 Review",
    question: "When will I receive my 360 Review results?",
    answer: "Results will be released collectively for all employees after the deadline."
  },
  {
    category: "360 Review",
    question: "Can I nominate employee of Gentari in Malaysia/ India/ Australia/ Thailand/ Taiwan, or PETRONAS employee?",
    answer: "Yes, you can."
  },
  {
    category: "360 Review",
    question: "Can I nominate external assessor outside of Gentari/PETRONAS?",
    answer: "No, you cannot."
  },
  {
    category: "360 Review",
    question: "As assessor, can I change feedback written and rating assigned once I have submitted the form?",
    answer: "No, you are encouraged to double check the name ensuring feedback given to the right people. You are also encouraged to provide your assessment in a calm environment to ensure objective and focused feedback being given."
  },
  // End-Year Review
  {
    category: "End-Year Review",
    question: "What steps are involved in the End-Year Review?",
    answer: "You will provide a self-assessment, have a review session with your superior and submit your rating in the system."
  },
  {
    category: "End-Year Review",
    question: "What happens if I don't submit my review?",
    answer: "Non-submission may impact your STIP (Short-Term Incentive Plan) and increment eligibility."
  },
  {
    category: "End-Year Review",
    question: "Who reviews and approves the final rating?",
    answer: "Your superior approves it, and it is then reviewed by higher-level managers (L2) and calibrated during AA and GTC sessions."
  },
  {
    category: "End-Year Review",
    question: "Can my rating change after calibration?",
    answer: "Yes, ratings may be amended based on calibration or GTC deliberations. HRBP will communicate any changes."
  },
  {
    category: "End-Year Review",
    question: "What is the process for identifying employees for PIP (Performance Improvement Plan)?",
    answer: "After final ratings are consolidated, HR will identify potential PIP cases based on performance outcomes."
  },
  {
    category: "End-Year Review",
    question: "As superior, what if I have accidentally approved the wrong End-Year Review and require my subordinate to amend?",
    answer: "You are required to reach out to the HR support team to enable the form for you to again approve or reject."
  },
  {
    category: "End-Year Review",
    question: "What happens if the superior does not approve it on time?",
    answer: "You will not receive your individual rating or bonus payout. We encourage you to complete the approval within the timeline set by the Talent Department."
  },
];

const FAQ: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openQuestion, setOpenQuestion] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Get unique categories
  const categories = Array.from(new Set(faqData.map(item => item.category)));

  // Global search across all categories with suggestions
  const searchSuggestions: SearchSuggestion[] = searchQuery.trim() 
    ? faqData
        .map((item, index) => ({ ...item, index }))
        .filter(item => 
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 5) // Limit to 5 suggestions
    : [];

  // Filter FAQs based on active category and search query
  const filteredFAQs = faqData.filter((item) => {
    const matchesCategory = activeCategory === null || item.category === activeCategory;
    const matchesSearch = searchQuery === "" || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }).map((item) => ({ ...item, originalIndex: faqData.indexOf(item) }));

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(e.target.value.trim().length > 0);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setSearchQuery("");
    setShowSuggestions(false);
    setActiveCategory(null); // Show all categories when clicking a suggestion
    setOpenQuestion(suggestion.index);
    
    // Scroll to the question after a short delay
    setTimeout(() => {
      const element = document.getElementById(`faq-${suggestion.index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCategoryClick = (category: string) => {
    setActiveCategory(category);
    setOpenQuestion(null);
    setSearchQuery("");
    setShowSuggestions(false);
  };

  const clearFilters = () => {
    setActiveCategory(null);
    setSearchQuery("");
    setShowSuggestions(false);
    setOpenQuestion(null);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-primary-600 p-3 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Frequently Asked Questions</h1>
              <p className="text-gray-600 mt-1">Employee Performance Management System</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        {/* Search Bar with Suggestions */}
        <div className="mb-6" ref={searchRef}>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search across all categories..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
              className="block w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-all"
            />
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-2 bg-white rounded-lg border border-gray-200 max-h-96 overflow-y-auto custom-scrollbar">
                <div className="p-2">
                  <div className="text-xs font-medium text-gray-500 px-3 py-2">
                    {searchSuggestions.length} result{searchSuggestions.length !== 1 ? 's' : ''} found
                  </div>
                  {searchSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-3 py-3 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <svg className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {suggestion.question}
                          </div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                            {suggestion.category}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active Filters */}
        {(activeCategory || searchQuery) && (
          <div className="mb-6 flex items-center flex-wrap gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {activeCategory && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                {activeCategory}
                <button
                  onClick={() => setActiveCategory(null)}
                  className="ml-2 hover:text-primary-900"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                Search: "{searchQuery}"
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setShowSuggestions(false);
                  }}
                  className="ml-2 hover:text-primary-900"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Category Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Categories</h2>
                <nav className="space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
                  <button
                    onClick={() => setActiveCategory(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeCategory === null
                        ? 'bg-primary-600 text-white font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryClick(category)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeCategory === category
                          ? 'bg-primary-600 text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* FAQ Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200">
              {filteredFAQs.length === 0 ? (
                <div className="p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Try adjusting your search or browse different categories
                  </p>
                  <button
                    onClick={clearFilters}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredFAQs.map((faq) => (
                    <div 
                      key={faq.originalIndex} 
                      id={`faq-${faq.originalIndex}`}
                      className="p-6"
                    >
                      <button
                        onClick={() => toggleQuestion(faq.originalIndex)}
                        className="w-full flex justify-between items-start text-left"
                      >
                        <div className="flex-1 pr-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                              {faq.category}
                            </span>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {faq.question}
                          </h3>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className={`h-5 w-5 text-primary-600 transition-transform ${
                              openQuestion === faq.originalIndex ? 'transform rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {openQuestion === faq.originalIndex && (
                        <div className="mt-4 pl-6 border-l-2 border-primary-600">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Help Section */}
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Still need help?</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    If you couldn't find the answer you're looking for, please contact the IT Helpdesk or HR support team for assistance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default FAQ;